import { useEffect } from "react";

interface LiveChatWidgetProps {
  // Base URL of the external live chat bundle, e.g.
  // https://d1h276491ac549.cloudfront.net/dist/webchat.js
  scriptUrl: string;
  // Live chat integration key (query param `appKey`).
  appKey: string;
}

/**
 * Injects the Siena live chat widget by loading its external bundle.
 *
 * Once loaded, the bundle exposes a documented global API on `window`
 * (see siena-web/packages/live-chat/WIDGET_LAUNCH_API.md):
 *   - window.SienaLaunchChat(message?, email?)
 *   - window.SienaLaunchFullscreen(message?, email?)
 *   - window.SienaLiveChatWidget.launch / launchFullscreen / isReady
 *
 * The docs-site's "Ask AI" / "Ask me anything" buttons call those via the
 * `openLiveChat()` helper in `src/lib/liveChat.ts`.
 */
export const LiveChatWidget: React.FC<LiveChatWidgetProps> = ({
  scriptUrl,
  appKey,
}) => {
  useEffect(() => {
    if (!scriptUrl || !appKey) {
      // eslint-disable-next-line no-console
      console.warn(
        "[LiveChatWidget] not mounting — scriptUrl or appKey missing",
        { scriptUrl, appKey }
      );
      return;
    }

    const id = "siena-chat-widget";
    if (document.getElementById(id)) return;

    const url = `${scriptUrl}${scriptUrl.includes("?") ? "&" : "?"
      }appKey=${encodeURIComponent(appKey)}`;

    const script = document.createElement("script");
    script.id = id;
    script.src = url;
    script.async = true;
    script.onerror = (e) => {
      // eslint-disable-next-line no-console
      console.error("[LiveChatWidget] failed to load", url, e);
    };
    document.body.appendChild(script);

    // No cleanup: the widget bundle attaches an iframe + global listeners to
    // `window` that we cannot cleanly tear down. Removing just the <script>
    // tag (the only thing this effect owns) would leave the iframe/globals
    // orphaned, and any later re-mount would re-execute the script on top of
    // that stale state — the bundle doesn't expect to be re-initialised and
    // stops responding to its public launch API. Keeping the mount stable
    // across SPA navigations (see Router) ensures this effect runs exactly
    // once per page load, so the lack of cleanup is safe.
  }, [scriptUrl, appKey]);

  return null;
};
